package com.swetdot.swetgram.ui.chat

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.animate
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.rememberTransformableState
import androidx.compose.foundation.gestures.transformable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.isSpecified
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.window.Dialog
import com.swetdot.swetgram.domain.Message
import com.swetdot.swetgram.ui.design.Motion
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIconButton
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetBlackBadge
import com.swetdot.swetgram.ui.design.SwetImagePhase
import com.swetdot.swetgram.ui.design.SwetEasing
import com.swetdot.swetgram.ui.design.rememberSwetImage
import com.swetdot.swetgram.ui.design.swetSheetDialogProperties
import com.swetdot.swetgram.ui.design.swetSkeletonPulse
import com.swetdot.swetgram.ui.design.swetPressable
import com.swetdot.swetgram.ui.design.swetTween
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.chat_action_close
import swetgram.shared.generated.resources.chat_action_retry
import swetgram.shared.generated.resources.chat_message_react
import swetgram.shared.generated.resources.chat_photo
import swetgram.shared.generated.resources.chat_photo_unavailable
import kotlin.math.max

private const val MEDIA_MIN_SCALE = 1f
private const val MEDIA_DOUBLE_TAP_SCALE = 2.5f
private const val MEDIA_MAX_SCALE = 4f
private const val MEDIA_DISMISS_FRACTION = 0.18f

/** The largest translation that still keeps a scaled, fitted photo covering its viewport. */
internal data class MediaPanBounds(val x: Float, val y: Float)

internal fun mediaPanBounds(viewport: IntSize, imageRatio: Float?, scale: Float): MediaPanBounds {
    if (viewport.width <= 0 || viewport.height <= 0 || scale <= MEDIA_MIN_SCALE) {
        return MediaPanBounds(0f, 0f)
    }
    val safeRatio = imageRatio?.takeIf { it.isFinite() && it > 0f } ?: 1f
    val viewportRatio = viewport.width.toFloat() / viewport.height.toFloat()
    val baseWidth: Float
    val baseHeight: Float
    if (viewportRatio > safeRatio) {
        baseHeight = viewport.height.toFloat()
        baseWidth = baseHeight * safeRatio
    } else {
        baseWidth = viewport.width.toFloat()
        baseHeight = baseWidth / safeRatio
    }
    return MediaPanBounds(
        x = max(0f, (baseWidth * scale - viewport.width) / 2f),
        y = max(0f, (baseHeight * scale - viewport.height) / 2f),
    )
}

internal fun clampMediaOffset(offset: Offset, bounds: MediaPanBounds): Offset = Offset(
    // Avoid preserving IEEE -0.0 on an axis that cannot pan. Besides making state comparisons
    // stable, this prevents a no-op graphics translation from looking changed to snapshot state.
    x = if (bounds.x == 0f) 0f else offset.x.coerceIn(-bounds.x, bounds.x),
    y = if (bounds.y == 0f) 0f else offset.y.coerceIn(-bounds.y, bounds.y),
)

internal fun shouldDismissMedia(dragY: Float, viewportHeight: Int): Boolean =
    viewportHeight > 0 && dragY >= viewportHeight * MEDIA_DISMISS_FRACTION

/**
 * Full-screen photo viewer owned by the chat UI rather than navigation or its view model.
 *
 * The message stays on the conversation route underneath this dialog. Closing therefore restores
 * the exact list position and composer state, while the platform dialog supplies the expected Back
 * behaviour on Android and iOS. Zoom and pan are deliberately local, short-lived UI state.
 */
@Composable
internal fun ChatMediaViewer(
    message: Message,
    zone: TimeZone,
    onDismiss: () -> Unit,
    onReact: (() -> Unit)?,
) {
    val closeLabel = stringResource(Res.string.chat_action_close)
    val photoLabel = stringResource(Res.string.chat_photo)
    val retryLabel = stringResource(Res.string.chat_action_retry)
    val settleSpec = swetTween<Float>(Motion.fast, SwetEasing.exit)
    val zoomSpec = swetTween<Float>(Motion.normal, SwetEasing.enter)
    val scope = rememberCoroutineScope()
    var viewport by remember(message.clientId) { mutableStateOf(IntSize.Zero) }
    var scale by remember(message.clientId) { mutableFloatStateOf(MEDIA_MIN_SCALE) }
    var imageOffset by remember(message.clientId) { mutableStateOf(Offset.Zero) }
    var dismissOffsetY by remember(message.clientId) { mutableFloatStateOf(0f) }
    var controlsVisible by remember(message.clientId) { mutableStateOf(true) }
    var retryKey by remember(message.clientId) { mutableIntStateOf(0) }

    Dialog(
        onDismissRequest = onDismiss,
        properties = swetSheetDialogProperties(
            dismissOnBackPress = true,
            dismissOnClickOutside = false,
            usePlatformDefaultWidth = false,
        ),
    ) {
        key(retryKey) {
            val media = rememberSwetImage(message.imageUrl.orEmpty(), ContentScale.Fit)
            val transformState = rememberTransformableState { centroid, zoomChange, panChange, _ ->
                val nextScale = (scale * zoomChange).coerceIn(MEDIA_MIN_SCALE, MEDIA_MAX_SCALE)
                if (nextScale > MEDIA_MIN_SCALE) {
                    val center = Offset(viewport.width / 2f, viewport.height / 2f)
                    val zoomedOffset = if (centroid.isSpecified) {
                        imageOffset * zoomChange + (centroid - center) * (1f - zoomChange)
                    } else {
                        imageOffset
                    }
                    scale = nextScale
                    dismissOffsetY = 0f
                    val bounds = mediaPanBounds(viewport, media.aspectRatio, nextScale)
                    imageOffset = clampMediaOffset(zoomedOffset + panChange, bounds)
                } else {
                    scale = MEDIA_MIN_SCALE
                    imageOffset = Offset.Zero
                    // Only a downward one-finger movement dismisses. Horizontal movement and an
                    // upward pull do not steal meaning from the conversation's own reply gesture.
                    dismissOffsetY = (dismissOffsetY + panChange.y).coerceAtLeast(0f)
                }
            }

            LaunchedEffect(transformState.isTransformInProgress) {
                if (transformState.isTransformInProgress || dismissOffsetY == 0f) return@LaunchedEffect
                if (shouldDismissMedia(dismissOffsetY, viewport.height)) {
                    onDismiss()
                } else {
                    val start = dismissOffsetY
                    animate(
                        initialValue = start,
                        targetValue = 0f,
                        animationSpec = settleSpec,
                    ) { value, _ -> dismissOffsetY = value }
                }
            }

            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black)
                    .onSizeChanged { viewport = it }
                    .clipToBounds(),
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .graphicsLayer {
                            scaleX = scale
                            scaleY = scale
                            translationX = imageOffset.x
                            translationY = imageOffset.y + dismissOffsetY
                            alpha = 1f - (dismissOffsetY / viewport.height.coerceAtLeast(1))
                                .coerceIn(0f, 0.45f)
                        }
                        .pointerInput(message.clientId) {
                            detectTapGestures(
                                onTap = { controlsVisible = !controlsVisible },
                                onDoubleTap = { tapPosition ->
                                    val targetScale = if (scale > MEDIA_MIN_SCALE) {
                                        MEDIA_MIN_SCALE
                                    } else {
                                        MEDIA_DOUBLE_TAP_SCALE
                                    }
                                    val startScale = scale
                                    val startOffset = imageOffset
                                    val center = Offset(viewport.width / 2f, viewport.height / 2f)
                                    scope.launch {
                                        animate(
                                            initialValue = 0f,
                                            targetValue = 1f,
                                            animationSpec = zoomSpec,
                                        ) { fraction, _ ->
                                            val animatedScale =
                                                startScale + (targetScale - startScale) * fraction
                                            scale = animatedScale
                                            imageOffset = if (targetScale == MEDIA_MIN_SCALE) {
                                                startOffset * (1f - fraction)
                                            } else {
                                                val zoomChange = animatedScale / startScale
                                                val desired = startOffset * zoomChange +
                                                    (tapPosition - center) * (1f - zoomChange)
                                                clampMediaOffset(
                                                    desired,
                                                    mediaPanBounds(viewport, media.aspectRatio, animatedScale),
                                                )
                                            }
                                        }
                                    }
                                },
                            )
                        }
                        .transformable(state = transformState)
                        .semantics { contentDescription = photoLabel },
                    contentAlignment = Alignment.Center,
                ) {
                    when (media.phase) {
                        SwetImagePhase.Loading -> Box(
                            Modifier
                                .fillMaxSize()
                                .swetSkeletonPulse()
                                .background(Color(0xFF151515)),
                        )

                        SwetImagePhase.Loaded -> Image(
                            painter = media.painter,
                            contentDescription = null,
                            contentScale = ContentScale.Fit,
                            modifier = Modifier.fillMaxSize(),
                        )

                        SwetImagePhase.Failed -> Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(Space.one),
                        ) {
                            Text(
                                text = stringResource(Res.string.chat_photo_unavailable),
                                color = Color.White,
                                style = MaterialTheme.typography.bodyMedium,
                            )
                            Text(
                                text = retryLabel,
                                color = Color.White,
                                style = MaterialTheme.typography.labelLarge,
                                modifier = Modifier
                                    .padding(Space.two)
                                    .swetPressable(
                                        onClick = { retryKey++ },
                                        onClickLabel = retryLabel,
                                    ),
                            )
                        }
                    }
                }

                AnimatedVisibility(
                    visible = controlsVisible,
                    enter = fadeIn(settleSpec),
                    exit = fadeOut(settleSpec),
                    modifier = Modifier.align(Alignment.TopStart),
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(Color.Black.copy(alpha = 0.55f))
                            .statusBarsPadding()
                            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Horizontal))
                            .padding(horizontal = Space.one, vertical = Space.one),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SwetIconButton(
                            contentDescription = closeLabel,
                            onClick = onDismiss,
                            tint = Color.White,
                        ) { tint ->
                            SwetIcon(
                                icon = SwetIcons.Close,
                                tint = tint,
                                contentDescription = null,
                                size = Sizes.iconMedium,
                            )
                        }
                        Column(Modifier.weight(1f)) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Text(
                                    text = message.sender.displayName,
                                    color = Color.White,
                                    style = MaterialTheme.typography.bodyMedium,
                                    fontWeight = FontWeight.SemiBold,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                    modifier = Modifier.weight(1f, fill = false),
                                )
                                if (message.sender.hasBlackBadge) {
                                    Spacer(Modifier.width(Space.half))
                                    SwetBlackBadge(tint = Color.White)
                                }
                            }
                            Text(
                                text = formatClockTime(message.sentAt, zone),
                                color = Color.White.copy(alpha = 0.72f),
                                style = MaterialTheme.typography.labelSmall,
                            )
                        }
                    }
                }

                AnimatedVisibility(
                    visible = controlsVisible,
                    enter = fadeIn(settleSpec),
                    exit = fadeOut(settleSpec),
                    modifier = Modifier.align(Alignment.BottomCenter),
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(Color.Black.copy(alpha = 0.55f))
                            .navigationBarsPadding()
                            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Horizontal))
                            .padding(horizontal = Space.two, vertical = Space.one),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = message.text.orEmpty(),
                            color = Color.White,
                            style = MaterialTheme.typography.bodyMedium,
                            maxLines = 3,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1f),
                        )
                        if (onReact != null) {
                            Spacer(Modifier.width(Space.one))
                            SwetIconButton(
                                contentDescription = stringResource(Res.string.chat_message_react),
                                onClick = onReact,
                                tint = Color.White,
                            ) { tint ->
                                SwetIcon(
                                    icon = SwetIcons.Reaction,
                                    tint = tint,
                                    contentDescription = null,
                                    size = Sizes.iconMedium,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
